// TIPSSSO (self-hosted ZITADEL) portal integration for TipsTrack.
//
// The SPA uses OIDC Authorization Code + PKCE against the central ZITADEL issuer
// (sso.tipsdata.co). Every value here is PUBLIC — a public SPA client carries no
// secret — so they are safe to bake into the bundle at build time.
//
// When the issuer/client id are absent (e.g. before a ZITADEL application is
// provisioned for tipstrack.com) the portal is "not configured" and the UI falls
// back to the legacy email/password login.
import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';

const issuer = import.meta.env.VITE_ZITADEL_ISSUER ?? '';
const clientId = import.meta.env.VITE_ZITADEL_CLIENT_ID ?? '';
// The ZITADEL project id (number) for TipsTrack. When present, the SPA requests the
// project-audience reserved scope so the project id is asserted into the access
// token's `aud` — which is what the backend pins as ZITADEL_AUDIENCE.
const projectId = import.meta.env.VITE_ZITADEL_PROJECT_ID ?? '';

/** True when the TIPSSSO portal is configured for this build. */
export const oidcEnabled = Boolean(issuer && clientId);

// project-roles claim + (when known) this app's project id in the token audience.
const scope = [
  'openid',
  'profile',
  'email',
  'urn:zitadel:iam:org:project:roles',
  projectId ? `urn:zitadel:iam:org:project:id:${projectId}:aud` : '',
]
  .filter(Boolean)
  .join(' ');

const settings: UserManagerSettings = {
  authority: issuer,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  // ZITADEL exact-matches registered post-logout URIs — keep the trailing slash
  // (`${origin}/`) or end-session rejects with `post_logout_redirect_uri invalid`.
  post_logout_redirect_uri: `${window.location.origin}/`,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew`,
  response_type: 'code',
  scope,
  automaticSilentRenew: true,
  loadUserInfo: false,
  monitorSession: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

// Constructed only when configured; callers must guard on `oidcEnabled`.
export const userManager: UserManager | null = oidcEnabled ? new UserManager(settings) : null;

/**
 * Begin the login redirect to the TIPSSSO portal. `returnTo` is round-tripped
 * through the OIDC `state` so the callback can land the user back where they
 * intended. Falls back to the legacy login page when the portal is not configured.
 */
export async function startSsoLogin(returnTo = '/'): Promise<void> {
  if (userManager) {
    await userManager.signinRedirect({ state: { returnTo } });
  } else {
    window.location.assign('/login');
  }
}

/** Clear the local OIDC session and redirect through the ZITADEL end-session flow. */
export async function ssoLogout(): Promise<void> {
  if (!userManager) return;
  try {
    await userManager.signoutRedirect();
  } catch {
    await userManager.removeUser();
    window.location.assign('/');
  }
}
